import React, { useCallback, useEffect, useRef, useState } from "react";

import { Storage } from "aws-amplify";

import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  Portal,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";

import { Button, useNotify } from "react-admin";
import { logError } from "../../utils/appUtils";
import { SignatureDialog } from "../../managerApp/resources/outboundShipments/SignatureDialog";

import { generateSignatureKey } from "../../utils/fileUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: `${theme.zIndex.modal + 1} !important`,
    color: "white",
  },
}));

const storeSignature = async ({ key, binaryImage, notify }) => {
  try {
    return await Storage.put(key, binaryImage, {
      level: "public",
      contentType: "image/png",
    });
  } catch (err) {
    logError("SignatureBlock:storeSignature", err, notify);
  }
};

const processS3Sig = (blob, fn) => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  reader.onloadend = function () {
    const base64data = reader.result;
    fn(base64data);
  };
};

export const SignatureBlock = ({
  form,
  formData,
  record,
  updateShipmentMini,
}) => {
  const classes = useStyles();
  const [isCarrierSignatureModalOpen, setCarrierSignatureModalOpen] =
    useState(false);
  const [carrierSignatureImg, setCarrierSignatureImg] = useState(null);
  const [invalidYardSignatureMessage, setInvalidYardSignatureMessage] =
    useState("");
  const carrierSignatureRef = useRef(null);

  const [isYardSignatureModalOpen, setYardSignatureModalOpen] = useState(false);
  const [yardSignatureImg, setYardSignatureImg] = useState(null);
  const [invalidCarrierSignatureMessage, setInvalidCarrierSignatureMessage] =
    useState("");
  const yardSignatureRef = useRef(null);

  const [saving, setSaving] = useState(false);

  const notify = useNotify();

  const addSignature = async ({
    signatureBinaryImage,
    signatureKey,
    onClose,
    fn,
  }) => {
    try {
      debugger;
      setSaving(true);
      const key = generateSignatureKey(window.appUser?.reloadYardId);
      const s3Res = await storeSignature({
        key,
        binaryImage: signatureBinaryImage,
        notify,
      });
      if (!s3Res?.key) {
        return;
      }
      form.change(signatureKey, key);
      if (typeof updateShipmentMini !== "function") {
        throw new Error("SignatureBlock requires updateShipmentMini(record)");
      }
      await updateShipmentMini({
        ...record,
        [signatureKey]: key,
        signedBy: window.appUser.fullName,
      });
      getSignature(key, fn);
    } catch (err) {
      logError(`SignatureBlock:addSignature::${signatureKey}`, err, notify);
    } finally {
      onClose();
      setSaving(false);
    }
  };

  const getSignature = useCallback(
    async (key, fn) => {
      if (!key) return;
      try {
        let data = await Storage.get(key, { download: true });
        processS3Sig(data.Body, fn);
      } catch (err) {
        logError("OutboundShipment::getSignature", err, notify);
      }
    },
    [notify]
  );

  useEffect(() => {
    if (record?.carrierSignatureKey) {
      getSignature(record.carrierSignatureKey, setCarrierSignatureImg);
    }
  }, [record?.carrierSignatureKey, getSignature]);

  useEffect(() => {
    if (record?.yardSignatureKey) {
      getSignature(record.yardSignatureKey, setYardSignatureImg);
    }
  }, [record?.yardSignatureKey, getSignature]);

  const toggleCarrierSignatureModal = () =>
    setCarrierSignatureModalOpen((prev) => !prev);

  const addCarrierSignature = ({ signatureBinaryImage }) => {
    if (!carrierSignatureRef.current) {
      setInvalidCarrierSignatureMessage("Invalid signature");
      return;
    }
    addSignature({
      signatureBinaryImage,
      signatureKey: "carrierSignatureKey",
      onClose: toggleCarrierSignatureModal,
      fn: setCarrierSignatureImg,
    });
  };

  const toggleYardSignatureModal = () =>
    setYardSignatureModalOpen((prev) => !prev);

  const addYardSignature = ({ signatureBinaryImage }) => {
    if (!yardSignatureRef.current) {
      setInvalidYardSignatureMessage("Invalid signature");
      return;
    }
    addSignature({
      signatureBinaryImage,
      signatureKey: "yardSignatureKey",
      onClose: toggleYardSignatureModal,
      fn: setYardSignatureImg,
    });
  };

  useEffect(() => {
    if (yardSignatureRef.current) {
      yardSignatureRef.current.fromDataURL(yardSignatureImg);
    }
  }, [yardSignatureRef, yardSignatureImg]);

  useEffect(() => {
    if (carrierSignatureRef.current) {
      carrierSignatureRef.current.fromDataURL(carrierSignatureImg);
    }
  }, [carrierSignatureRef, carrierSignatureImg]);
  return (
    <>
      <Portal>
        <Backdrop open={saving} className={classes.backdrop}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Portal>
      <Grid item xs={12} md={4}>
        <Box mt={1}>
          <Button
            label="Yard Signature"
            variant="outlined"
            size="large"
            fullWidth
            disabled={saving}
            onClick={toggleYardSignatureModal}
            endIcon={
              formData?.yardSignatureKey ? (
                <CheckIcon fontSize="large" />
              ) : (
                <ClearIcon fontSize="large" />
              )
            }
          />
        </Box>
        {isYardSignatureModalOpen && (
          <SignatureDialog
            open={isYardSignatureModalOpen}
            handleClose={toggleYardSignatureModal}
            saving={saving}
            signatureImg={yardSignatureImg}
            signatureRef={yardSignatureRef}
            addSignature={addYardSignature}
            invalidSignatureMessage={invalidYardSignatureMessage}
            clearSignature={() => {}}
          />
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <Box mt={1}>
          <Button
            label="Carrier Signature"
            variant="outlined"
            size="large"
            fullWidth
            disabled={saving}
            onClick={toggleCarrierSignatureModal}
            endIcon={
              formData?.carrierSignatureKey ? (
                <CheckIcon fontSize="large" />
              ) : (
                <ClearIcon fontSize="large" />
              )
            }
          />
        </Box>
        {isCarrierSignatureModalOpen && (
          <SignatureDialog
            open={isCarrierSignatureModalOpen}
            handleClose={toggleCarrierSignatureModal}
            saving={saving}
            signatureImg={carrierSignatureImg}
            signatureRef={carrierSignatureRef}
            addSignature={addCarrierSignature}
            invalidSignatureMessage={invalidCarrierSignatureMessage}
            clearSignature={() => {}}
          />
        )}
      </Grid>
    </>
  );
};
